/**
 * Match — Lifestyle preference matcher (V1.1)
 *
 * Discovery-mode counterpart to the eligibility checker on `/check`. Whereas
 * /check answers "which citizenships do I already qualify for", /match
 * answers "which countries fit the LIFE I want" — climate, coast, cost of
 * living, healthcare, safety, English usability.
 *
 * Flow:
 *   1. Visitor drag-ranks the six factors in order of importance
 *   2. If climate made it into the top of the rank, they pick a target band
 *      (1=cold ... 5=hot/tropical)
 *   3. Household modifier: solo / partner / kids
 *   4. Server returns a fit-scored shortlist of ~10 countries
 *
 * A visitor arriving from an existing eligibility report (`?report_id=`) also
 * gets accessibility annotations on the shortlist — "you're already eligible
 * for this one via marriage" — which is the wireframe's "matcher-as-lens"
 * pattern.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Compass, ChevronUp, ChevronDown, Loader2, Globe2, MapPin, Users,
  Sparkles, ArrowRight, Info, Sun,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { getMatchConfig, runMatch, logEvent } from "@/lib/api";

const CLIMATE_LABELS = {
  1: "Cold (Nordic winters)",
  2: "Cool (temperate northern)",
  3: "Temperate (four seasons)",
  4: "Warm (Mediterranean)",
  5: "Hot / tropical",
};

/** Compact ranked-list item. Users reorder via ▲ / ▼ buttons — full HTML5
 *  drag-and-drop is overkill for six items and hostile on touch devices. */
const RankRow = ({ item, index, total, onUp, onDown }) => (
  <div
    data-testid={`match-rank-row-${item.key}`}
    className="flex items-center gap-3 rounded-lg border bg-white px-3 py-2"
  >
    <span className="font-display text-lg font-semibold text-[hsl(var(--atlas-navy))] tabular-nums w-6">
      {index + 1}.
    </span>
    <span className="flex-1 text-sm text-[hsl(var(--atlas-navy))]">{item.label}</span>
    <button
      type="button"
      aria-label="Move up"
      disabled={index === 0}
      onClick={onUp}
      className="h-8 w-8 rounded-md border text-muted-foreground disabled:opacity-30 hover:bg-[hsl(var(--muted))]"
      data-testid={`match-rank-up-${item.key}`}
    >
      <ChevronUp className="h-4 w-4 mx-auto" />
    </button>
    <button
      type="button"
      aria-label="Move down"
      disabled={index === total - 1}
      onClick={onDown}
      className="h-8 w-8 rounded-md border text-muted-foreground disabled:opacity-30 hover:bg-[hsl(var(--muted))]"
      data-testid={`match-rank-down-${item.key}`}
    >
      <ChevronDown className="h-4 w-4 mx-auto" />
    </button>
  </div>
);

const ShortlistCard = ({ entry, rank }) => {
  const acc = entry.accessibility_score;
  return (
    <Card
      data-testid={`match-shortlist-row-${entry.country}`}
      className="p-4 bg-white flex items-start gap-4 card-hover"
    >
      <div className="shrink-0 h-10 w-10 rounded-xl bg-[hsl(var(--atlas-navy))] text-white flex items-center justify-center font-display font-semibold tabular-nums">
        {rank}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-display text-lg font-semibold text-[hsl(var(--atlas-navy))]">
            {entry.country}
          </span>
          <span className="text-xs text-muted-foreground">Fit {entry.fit_score}/100</span>
          {acc != null && (
            <Badge
              className="bg-[hsl(var(--atlas-teal))]/15 text-[hsl(var(--atlas-navy))] border border-[hsl(var(--atlas-teal))]/40 gap-1"
              data-testid={`match-shortlist-row-${entry.country}-accessible`}
            >
              <Sparkles className="h-3 w-3" /> Also accessible ({Math.round(acc)}/100)
            </Badge>
          )}
        </div>
        {entry.top_pathway && (
          <div className="text-xs text-muted-foreground mt-0.5">
            Your route: {entry.top_pathway.pathway_name}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-[hsl(var(--atlas-slate))]">
          {Object.entries(entry.breakdown || {}).slice(0, 4).map(([factor, meta]) => (
            <span key={factor} className="inline-flex items-center gap-1">
              <span className="uppercase tracking-wide text-muted-foreground">{factor.replace("_", " ")}</span>
              <span className="font-medium text-[hsl(var(--atlas-navy))]">
                {meta.descriptor} ({meta.band}/5)
              </span>
            </span>
          ))}
        </div>
      </div>
    </Card>
  );
};

export default function Match() {
  const [config, setConfig] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [climatePref, setClimatePref] = useState(3);
  const [household, setHousehold] = useState("solo");
  const [shortlist, setShortlist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  const reportId = params.get("report_id") || null;

  // Load the factor + household config once. Set the default ranking to the
  // canonical factor order — visitors adjust from there.
  useEffect(() => {
    getMatchConfig()
      .then((c) => {
        setConfig(c);
        setRanking(c.factors.map((f) => f.key));
        logEvent("match_opened", reportId, { with_report: !!reportId });
      })
      .catch(() => toast.error("Could not load matcher configuration"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const factorLookup = useMemo(() => {
    const m = new Map();
    (config?.factors || []).forEach((f) => m.set(f.key, f));
    return m;
  }, [config]);

  const move = (idx, delta) => {
    const next = [...ranking];
    const [item] = next.splice(idx, 1);
    next.splice(idx + delta, 0, item);
    setRanking(next);
  };

  const submit = async () => {
    setLoading(true);
    try {
      const climateInTop = ranking.indexOf("climate") <= 2;
      const res = await runMatch({
        ranking,
        climate_pref: climateInTop ? climatePref : null,
        household,
        limit: 10,
        report_id: reportId,
      });
      setShortlist(res.shortlist || []);
      logEvent("match_submitted", reportId, {
        top_factor: ranking[0], household, count: res.count,
      });
    } catch {
      toast.error("Could not compute your matches — try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  if (!config) {
    return (
      <div className="py-24 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--atlas-navy))]" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10" data-testid="match-page">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <Badge variant="secondary" className="mb-3 gap-1.5">
          <Compass className="h-3.5 w-3.5 text-[hsl(var(--atlas-teal))]" /> Preference matcher
        </Badge>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-[hsl(var(--atlas-navy))]">
          Which countries fit the life you want?
        </h1>
        <p className="mt-2 text-muted-foreground max-w-2xl">
          Rank what matters to you. We&apos;ll shortlist countries that fit — using
          climate zones, cost of living, healthcare, safety, coast access, and
          English usability. Two-minute quiz, free, no signup.
        </p>
        {reportId && (
          <div
            className="mt-4 inline-flex items-center gap-2 rounded-lg border border-[hsl(var(--atlas-teal))]/40 bg-[hsl(var(--atlas-teal))]/10 px-3 py-1.5 text-xs"
            data-testid="match-report-link-active"
          >
            <Info className="h-3.5 w-3.5 text-[hsl(var(--atlas-teal))]" />
            Linked to your eligibility report — matches you already qualify for will be highlighted.
          </div>
        )}
      </motion.div>

      <div className="mt-8 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6">
        {/* --- Quiz column ------------------------------------------------ */}
        <div className="space-y-6">
          <Card className="p-6 bg-white">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-[hsl(var(--atlas-navy))]" />
              <h3 className="font-display font-semibold text-[hsl(var(--atlas-navy))]">Household</h3>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2" data-testid="match-household-selector">
              {config.households.map((h) => (
                <button
                  key={h.key}
                  type="button"
                  onClick={() => setHousehold(h.key)}
                  data-testid={`match-household-${h.key}`}
                  className={[
                    "rounded-lg border px-3 py-3 text-sm transition-colors",
                    household === h.key
                      ? "border-[hsl(var(--atlas-navy))] bg-[hsl(var(--atlas-navy))] text-white"
                      : "hover:bg-[hsl(var(--muted))] text-[hsl(var(--atlas-navy))]",
                  ].join(" ")}
                >
                  {h.label}
                </button>
              ))}
            </div>
          </Card>

          <Card className="p-6 bg-white">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[hsl(var(--atlas-navy))]" />
              <h3 className="font-display font-semibold text-[hsl(var(--atlas-navy))]">
                Rank what matters, most first
              </h3>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Use the arrows to reorder. Ranking drives how much each factor weighs in your score.
            </p>
            <div className="mt-4 space-y-2" data-testid="match-ranking-list">
              {ranking.map((key, i) => {
                const item = factorLookup.get(key);
                if (!item) return null;
                return (
                  <RankRow
                    key={key}
                    item={item}
                    index={i}
                    total={ranking.length}
                    onUp={() => move(i, -1)}
                    onDown={() => move(i, 1)}
                  />
                );
              })}
            </div>
          </Card>

          <Card className="p-6 bg-white">
            <div className="flex items-center gap-2">
              <Sun className="h-4 w-4 text-[hsl(var(--atlas-navy))]" />
              <h3 className="font-display font-semibold text-[hsl(var(--atlas-navy))]">Climate preference</h3>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Only used if climate is in your top three. Move the slider to what feels like home weather.
            </p>
            <div className="mt-4">
              <Slider
                value={[climatePref]}
                min={1}
                max={5}
                step={1}
                onValueChange={(v) => setClimatePref(v[0])}
                data-testid="match-climate-slider"
              />
              <div
                className="mt-2 text-sm text-[hsl(var(--atlas-navy))] font-medium"
                data-testid="match-climate-label"
              >
                {CLIMATE_LABELS[climatePref]}
              </div>
            </div>
          </Card>

          <Button
            onClick={submit}
            disabled={loading}
            className="w-full bg-[hsl(var(--atlas-navy))] hover:bg-[hsl(var(--atlas-navy-2))] text-white h-12 text-base"
            data-testid="match-submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Finding your matches…
              </>
            ) : (
              <>
                Show my shortlist <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>

        {/* --- Shortlist column ------------------------------------------- */}
        <div>
          <Card className="p-6 bg-white min-h-[400px]" data-testid="match-shortlist-card">
            <div className="flex items-center gap-2">
              <Globe2 className="h-4 w-4 text-[hsl(var(--atlas-navy))]" />
              <h3 className="font-display font-semibold text-[hsl(var(--atlas-navy))]">Your shortlist</h3>
              {shortlist && (
                <Badge variant="secondary" className="ml-auto">
                  {shortlist.length} countries
                </Badge>
              )}
            </div>
            {!shortlist ? (
              <div className="mt-6 rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                Set your preferences on the left and click <b>Show my shortlist</b>.
                <br />
                We score against 50 relocation-relevant countries with cited data.
              </div>
            ) : shortlist.length === 0 ? (
              <div className="mt-6 rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                No countries scored above zero — try adjusting your ranking.
              </div>
            ) : (
              <div className="mt-4 space-y-3" data-testid="match-shortlist">
                {shortlist.map((entry, i) => (
                  <ShortlistCard key={entry.country} entry={entry} rank={i + 1} />
                ))}
                <div className="pt-2 text-xs text-muted-foreground">
                  Coverage: 50 countries with lifestyle bands cited from World Bank PPP,
                  Numbeo, Global Peace Index, and EF EPI. More countries coming.
                </div>
                {!reportId && (
                  <div className="pt-3">
                    <Link to="/check">
                      <Button
                        variant="outline"
                        className="w-full"
                        data-testid="match-to-eligibility-cta"
                      >
                        See which of these you can actually claim
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
